import type { ReactNode } from 'react';
import merkeSvg from '../../../theme/merke.svg?raw';
import { merke, skala } from '../../../theme/theme.ts';

/**
 * Merket «Ringen» (M0.4) — tegnet fra theme/merke.svg via Vite `?raw`, aldri en kopi i
 * panel/. Reglene kommer fra theme.ts: under `merke.minStorrelsePx` står ordmerket
 * alene, og fargen er `currentColor` så den er et token (action-primary, eller text-muted
 * dempet) og aldri en hex her. Inline SVG i DOM-en er ingen ressurs for CSP-en (K8).
 *
 * Innholdet er repoets egen fil, ikke innsendt tekst — derfor kan det settes som markup.
 * Kommentaren og rotelementet strippes: wrapperen eier mål og roller.
 */
const TEGNING = merkeSvg
  .replace(/<!--[\s\S]*?-->/g, '')
  .replace(/<svg\b[^>]*>/, '')
  .replace(/<\/svg>\s*$/, '')
  .replace(/\s+/g, ' ')
  .replace(/>\s+</g, '><')
  .trim();

export type MerkeStorrelse = keyof typeof skala.space;

export function Merke({ storrelse = 'lg', dempet = false, alene = false }: { storrelse?: MerkeStorrelse; dempet?: boolean; alene?: boolean }) {
  if (skala.space[storrelse] < merke.minStorrelsePx) return null;
  const tilgjengelighet = alene ? { role: 'img', 'aria-label': 'Innspel' } : { 'aria-hidden': true };
  return (
    <svg
      viewBox={merke.viewBox}
      fill="none"
      className={`panel-merke-ikon panel-merke-ikon-${storrelse}${dempet ? ' panel-merke-ikon-dempet' : ''}`}
      {...tilgjengelighet}
      dangerouslySetInnerHTML={{ __html: TEGNING }}
    />
  );
}

/**
 * Merke + ordmerke som én enhet, med en linje under (leietakernavn i skallet, «Adminpanel»
 * i kortene). `linje` legger barna på SAMME rad i stedet for under: stripen under 900 px er
 * 56 px høy og har ikke to linjer. Ringen står aldri alene fra ordmerket (DD-48, avvik 10),
 * så det er hele enheten som flytter, ikke bare navnet.
 */
export function Merkehode({ storrelse, linje = false, children }: { storrelse: MerkeStorrelse; linje?: boolean; children?: ReactNode }) {
  return (
    <div className={linje ? 'panel-merke panel-merke-linje' : 'panel-merke'} data-testid="merke">
      <div className="panel-merke-rad">
        <Merke storrelse={storrelse} />
        <span className="panel-merke-navn">Innspel</span>
        {linje ? children : null}
      </div>
      {linje ? null : children}
    </div>
  );
}
